import { useAppSelector } from "@app/hooks";
import { RootState } from "@app/store";
import { PropertyManifestEntry } from "@features/home-manifest/types";
import { ManifestDevice, Sensor } from "@features/plan-manifest/types";
import Add from "@mui/icons-material/Add";
import QrCodeScanner from "@mui/icons-material/QrCodeScanner";
import Delete from "@mui/icons-material/Delete";
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RestartAlt from '@mui/icons-material/RestartAlt';
import { Badge, ButtonGroup, IconButton, Stack, Tooltip, Typography, Chip } from "@mui/material";
import { useMemo } from "react";
import SensorToPair from "./SensorToPair";
import { useProvisioning } from "./ProvisioningContext";

/* Displays Manifest Entry */
const EntryToPair = ({ entry }: { entry: PropertyManifestEntry }) => {
  const property = useAppSelector((state: RootState) => state.property);
  const {
    entries,
    onRemoveDevice,
    onAddDevice,
    onAddSmartstartDevice,
    onResetDevice,
    onManualLinkDevice,
    onManualUnlinkDevice,
  } = useProvisioning();

  const devices = useMemo(() => property.devices || [], [property.devices]);

  const unlinkedDeviceCount = useMemo(() => {
    const linkedDeviceIds: Set<string> = new Set(
      entries
        .map(e => e.gateway_device_id)
        .filter((e): e is string => !!e)
    );
    const unlinkedDeviceIds: Set<string> = new Set(
      devices
        .filter(d => !linkedDeviceIds.has(d.data.device_id))
        .map(d => d.data.device_id)
    );
    return unlinkedDeviceIds.size;
  }, [entries, devices]);

  return (
    <Stack sx={{ width: 'inherit', height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        {entry.gateway_device_id ? (
          <Chip label="Paired" color="success" size="small" sx={{ mr: 1 }} />
        ) : (
          <Chip label="Not Paired" color="warning" size="small" sx={{ mr: 1 }} />
        )}
        {(entry.device as ManifestDevice).friendly_name} &mdash; {entry.property_area}
        <ButtonGroup sx={{ ml: 2 }}>
          <Tooltip title="Scan QR code for device">
            <span>
              <IconButton disabled={!!entry.gateway_device_id} size="large" color="primary" onClick={() => onAddSmartstartDevice(entry)}>
                <QrCodeScanner />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Add Device">
            <span>
              <IconButton disabled={!!entry.gateway_device_id} size="large" color="primary" onClick={() => onAddDevice(entry)}>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset Device">
            <span>
              <IconButton size="large" color="secondary" onClick={() => onResetDevice(entry)}>
                <RestartAlt />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Manually unlink device">
            <span>
              <IconButton disabled={!entry.gateway_device_id} size="large" color="secondary" onClick={() => onManualUnlinkDevice(entry)}>
                <LinkOffIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Manually link device">
            <span>
              <IconButton disabled={!!entry.gateway_device_id} size="large" color="secondary" onClick={() => onManualLinkDevice(entry)}>
                <Badge badgeContent={unlinkedDeviceCount}>
                  <LinkIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove Device">
            <span>
              <IconButton size="large" color="error" onClick={() => onRemoveDevice(entry)}>
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        </ButtonGroup>
      </Typography>
      <Stack sx={{ height: '31em', overflowY: 'scroll' }}>
        <Stack spacing={1} sx={{ padding: 1 }}>
          {((entry.device as ManifestDevice).sensors || []).map((ss: Sensor) => (
            <SensorToPair
              key={`pairing-${ss.order}-${entry.manifest_entry_id}`}
              entry={entry}
              sensor={ss}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EntryToPair;